package core.dateformat.domain

import core.dateformat.utils.dateDiffInDays
import core.dateformat.utils.formatDateToDDMMYYYY
import core.dateformat.utils.formatDateToMMDDYYYY
import core.dateformat.utils.formatMillisToDDMMYYYY
import core.dateformat.utils.formatMillisToMMDDYYYY
import core.dateformat.utils.formatNumber
import core.dateformat.utils.isoDateToDayOfTheWeek
import core.dateformat.utils.parseLocalDateOrNull
import com.essntl.core.utils.utils.capitalizeFirstLetter
import core.datastore.data.DatastoreManager
import core.dateformat.data.DateConfig
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.koin.core.annotation.Single

@Single
class DateFormatUseCase(
    private val datastoreManager: DatastoreManager,
) {
    fun setDateFormat(dateFormat: String? = null) {
        val dateFormat = dateFormat ?: datastoreManager.getData(DatastoreManager.DATE_FORMAT)
        val date = if (dateFormat.isNullOrEmpty())
            DateFormat.MONTH_DAY.format else
            dateFormat

        DateConfig.update(date)
    }

    fun getDateFormat(): String {
        val dateFormat = datastoreManager.getData(DatastoreManager.DATE_FORMAT)
        return if (dateFormat.isNullOrEmpty())
            DateFormat.MONTH_DAY.format else
            dateFormat
    }

    fun changeDateFormat(dateFormat: String) {
        if (dateFormat == DateFormat.MONTH_DAY.format ||
            dateFormat == DateFormat.DAY_MONTH.format
        ) {
            datastoreManager.saveData(DatastoreManager.DATE_FORMAT, dateFormat)
            setDateFormat(dateFormat)
        }
    }

    fun formatDate(
        date: String?,
        separator: String = "/",
        excludeYear: Boolean = false,
    ): String =
        when (DateConfig.dateFormat) {
            DateFormat.MONTH_DAY.format -> formatDateToMMDDYYYY(date, separator, excludeYear)
            DateFormat.DAY_MONTH.format -> formatDateToDDMMYYYY(date, separator, excludeYear)
            else -> ""
        }

    fun formatDateDialog(
        millis: Long,
        separator: String = "/",
    ): String =
        when (DateConfig.dateFormat) {
            DateFormat.MONTH_DAY.format -> formatMillisToMMDDYYYY(millis, separator)
            DateFormat.DAY_MONTH.format -> formatMillisToDDMMYYYY(millis, separator)
            else -> ""
        }

    fun formatIsoDateRange(
        fromDate: String?,
        toDate: String?,
    ): ArrayList<HashMap<String, String>> {
        val formattedFromDate = formatDate(fromDate, separator = ".", excludeYear = true)
        val formattedToDate = formatDate(toDate, separator = ".", excludeYear = true)

        if (formattedFromDate == formattedToDate)
            return arrayListOf(
                hashMapOf(
                    "date" to formattedFromDate,
                    "day" to isoDateToDayOfTheWeek(fromDate),
                ),
            )

        val arrayList: ArrayList<HashMap<String, String>> = arrayListOf()

        if (formattedFromDate.isNotEmpty())
            arrayList.add(
                hashMapOf(
                    "date" to formattedFromDate,
                    "day" to isoDateToDayOfTheWeek(fromDate),
                ),
            )

        if (formattedToDate.isNotEmpty())
            arrayList.add(
                hashMapOf(
                    "date" to formattedToDate,
                    "day" to isoDateToDayOfTheWeek(toDate),
                ),
            )

        return arrayList
    }

    fun formatDateRange(
        fromDate: String?,
        toDate: String?,
    ): String {
        if (fromDate == null ||
            toDate == null ||
            fromDate.length < 10 ||
            toDate.length < 10
        ) return ""

        val sb = StringBuilder()
        val fromLocalDate = parseLocalDateOrNull(fromDate) ?: return ""
        val toLocalDate = parseLocalDateOrNull(toDate) ?: return ""

        val fromMonth = fromLocalDate.month.name.lowercase().capitalizeFirstLetter().take(3)
        val toMonth = toLocalDate.month.name.lowercase().capitalizeFirstLetter().take(3)

        sb.append(fromMonth + " " + formatNumber(fromLocalDate.dayOfMonth))
        sb.append(" - ")
        if (fromLocalDate.monthNumber == toLocalDate.monthNumber) {
            sb.append(formatNumber(toLocalDate.dayOfMonth))
        } else {
            sb.append(toMonth + " " + formatNumber(toLocalDate.dayOfMonth))
        }
        sb.append(", ${toLocalDate.year}")

        return sb.toString()
    }

    fun formatDateToPrettyString(date: String?): String? {
        val todayDate = Clock.System.now().toLocalDateTime(TimeZone.UTC).date.toString()

        val dateDiff = dateDiffInDays(
            fromDate = date,
            toDate = todayDate,
        )

        if (date.isNullOrEmpty() || dateDiff == null)
            return null

        return formatDate(date, separator = ".", excludeYear = true)
    }
}